import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';

import Text from '_components/atomic/layouts/Card/Text';
import PrimaryButton from '_components/atomic/PrimaryButton';

const StyledOuterContainer = styled.div`
  max-width: ${theme.spaces.large32X * 10}px;
  width: 100%;
  padding-top: ${theme.spaces.large6X}px;

  position: relative;

  @media ${theme.devices.tablet} {
    padding: ${theme.spaces.large}px;
  }
`;

const StyledInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 768px;
  padding-top: ${theme.spaces.large18X}px;
  padding-bottom: ${theme.spaces.large18X}px;
  padding-left: ${theme.spaces.large24X}px;
  padding-right: ${theme.spaces.large24X}px;
  border-radius: 32px;
  background: ${`url(${config.basePath}/images/v4-man-and-boat.webp)`};
  background-repeat: no-repeat;
  background-blend-mode: hard-light;
  background-size: cover;

  position: relative;

  @media ${theme.devices.tablet} {
    min-height: unset;
    flex-direction: column;
    padding: ${theme.spaces.large}px;
    background-position: 84%;
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  min-height: 48px;
  max-width: 152px;
  font-size: ${theme.fontSizes.small}px;
`;

const StyledCurve1 = styled.div`
  height: ${theme.spaces.large28X}px;
  margin-top: ${theme.spaces.large16X}px;
  width: 100%;
  position: relative;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 40%;
    top: -1px;
    height: 80%;
    width: 50%;
    border-top-left-radius: 32px;
    border-top: 1px solid ${theme.colors.gray[200]};
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    right: 0;
    top: -100%;
    height: 100%;
    width: 50%;
    border-bottom-right-radius: 32px;
    border-bottom: 1px solid ${theme.colors.gray[200]};
  }

  @media ${theme.devices.tablet} {
    display: none;
  }
`;

const StyledLine1 = styled.div`
  display: block;
  position: absolute;
  right: -50px;
  top: 55px;
  height: 32px;
  width: 130px;
  border-top-right-radius: 32px;
  border-top: 1px solid ${theme.colors.gray[200]};
  transform: rotate(90deg);

  @media ${theme.devices.tablet} {
    display: none;
  }
`;

const StyledLine2 = styled.div`
  display: block;
  position: absolute;
  left: calc(35% - 6px);
  top: 213px;
  height: 1px;
  width: 130px;
  background-color: ${theme.colors.gray[200]};
  transform: rotate(109deg);

  @media ${theme.devices.tablet} {
    display: none;
  }
`;

const StyledCurve2 = styled.div`
  height: ${theme.spaces.large28X}px;
  margin-top: ${theme.spaces.large28X}px;
  width: 50%;
  position: relative;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 40%;
    top: -100%;
    height: 100%;
    width: 50%;
    border-bottom-left-radius: 32px;
    border-bottom: 1px solid ${theme.colors.gray[200]};
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 90%;
    top: -1px;
    height: 100%;
    width: 50%;
    border-top-right-radius: 32px;
    border-top: 1px solid ${theme.colors.gray[200]};
  }

  @media ${theme.devices.tablet} {
    display: none;
  }
`;

const StyledLine3 = styled.div`
  display: block;
  position: absolute;
  left: calc(66% - 4px);
  bottom: 70px;
  height: 32px;
  width: 100px;
  border-top-left-radius: 32px;
  border-top: 1px solid ${theme.colors.gray[200]};
  transform: rotate(90deg);

  @media ${theme.devices.tablet} {
    display: none;
  }
`;

const StyledLine4 = styled.div`
  display: block;
  position: absolute;
  left: calc(16% - 16px);
  bottom: 213px;
  height: 1px;
  width: 121px;
  background-color: ${theme.colors.gray[200]};
  transform: rotate(72deg);

  @media ${theme.devices.tablet} {
    display: none;
    width: 100%;
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  width: 100%;

  @media ${theme.devices.tablet} {
    justify-content: center;
  }
`;

const GetStartedSection = () => {
  return (
    <StyledOuterContainer>
      <StyledCurve1 />
      <StyledLine1 />
      <StyledLine2 />
      <StyledInnerContainer>
        <Text
          asTitle
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.white}
          bottomSpace={theme.spaces.large4X}
          desktop
        >
          INCORPORATE NOW
        </Text>
        <Text
          asTitle
          size={theme.fontSizes.small}
          weight={700}
          color={theme.colors.white}
          bottomSpace={theme.spaces.large21X * 2}
          mobile
        >
          INCORPORATE NOW
        </Text>
        <Text
          asTitle
          size={theme.fontSizes.large15X}
          weight={700}
          lineHeightRatio={1}
          color={theme.colors.white}
          bottomSpace={theme.spaces.large10X}
          desktop
        >
          READY TO
          <br />
          START?
        </Text>
        <Text
          asTitle
          size={theme.fontSizes.large6X}
          weight={700}
          lineHeightRatio={1}
          color={theme.colors.white}
          bottomSpace={theme.spaces.large}
          mobile
        >
          READY TO
          <br />
          START?
        </Text>
        <Text
          size={theme.fontSizes.largeX}
          weight={500}
          color={theme.colors.white}
          desktop
        >
          Getting started is easy. Click the
          <br />
          Get Started button below. Fill out
          <br />
          the basic form steps, and just
          <br /> minutes from now, you will have
          <br /> your new LLC.
        </Text>
        <Text
          size={theme.fontSizes.medium}
          weight={500}
          color={theme.colors.white}
          bottomSpace={theme.spaces.regular}
          mobile
        >
          Getting started is easy. Click the
          <br />
          Get Started button below. Fill out
          <br />
          the basic form steps, and just
          <br /> minutes from now, you will have
          <br /> your new LLC.
        </Text>
        <StyledLink href={ROUTES.incPackage}>
          <StyledPrimaryButton>GET STARTED</StyledPrimaryButton>
        </StyledLink>
      </StyledInnerContainer>
      <StyledCurve2 />
      <StyledLine3 />
      <StyledLine4 />
    </StyledOuterContainer>
  );
};

export default GetStartedSection;
